import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HardHat, Loader2 } from "lucide-react";

const BG = "https://images.pexels.com/photos/18162494/pexels-photo-18162494.jpeg";

function formatErr(detail) {
  if (!detail) return "Ocurrió un error. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-stretch">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={BG} alt="Construcción" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-11 w-11 rounded-md bg-orange-600 flex items-center justify-center">
              <HardHat className="h-6 w-6" />
            </div>
            <span className="font-display text-2xl font-bold tracking-tight">Control de Coladores</span>
          </div>
          <p className="text-slate-200 text-lg max-w-md leading-relaxed">
            Gestión semanal profesional de cuadrillas, pagos por m², liquidaciones y reportes en PDF.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-md bg-orange-600 flex items-center justify-center">
              <HardHat className="h-5 w-5 text-white" />
            </div>
            <span className="font-display text-xl font-bold">Control de Coladores</span>
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Iniciar sesión</h1>
          <p className="text-sm text-slate-500 mt-2 mb-8">Ingresa tus credenciales para continuar.</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">Correo</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required placeholder="tucorreo@ejemplo.com" className="mt-1.5" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Contraseña</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required placeholder="••••••••" className="mt-1.5" data-testid="login-password-input" />
            </div>
          </div>

          {error && <p className="text-sm text-red-600 mt-4" data-testid="login-error">{error}</p>}

          <Button type="submit" disabled={loading}
            className="w-full mt-6 bg-slate-900 hover:bg-slate-800 h-11" data-testid="login-submit-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
          </Button>
        </form>
      </div>
    </div>
  );
}
